import axios from '../axios'
import { CHANGELOGROUTES } from 'src/router/Api'
import { convertParams } from 'src/utils/utils'

type ChangelogPayload = {
	title?: string
	description?: string | null
	version?: string | null
	is_published?: boolean
}

const ALLOWED_CHANGELOG_FIELDS: (keyof ChangelogPayload)[] = [
	'title',
	'description',
	'version',
	'is_published',
]

const sanitizeChangelogPayload = (
	payload: Record<string, unknown>,
	isCreate = false,
): ChangelogPayload => {
	if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
		throw new Error('Payload must be an object')
	}

	const payloadKeys = Object.keys(payload)
	const invalidKeys = payloadKeys.filter(
		(key) => !ALLOWED_CHANGELOG_FIELDS.includes(key as any),
	)

	if (invalidKeys.length > 0) {
		throw new Error(`Invalid fields: ${invalidKeys.join(', ')}`)
	}

	if (isCreate && !payloadKeys.includes('title')) {
		throw new Error('title is required when creating changelog')
	}

	if (!isCreate && payloadKeys.length === 0) {
		throw new Error('At least one field is required for update')
	}

	const sanitizedPayload: ChangelogPayload = {}

	if (payload.title !== undefined) {
		sanitizedPayload.title = payload.title as string
	}

	if (payload.description !== undefined) {
		sanitizedPayload.description = payload.description as string | null
	}

	if (payload.version !== undefined) {
		sanitizedPayload.version = payload.version as string | null
	}

	if (payload.is_published !== undefined) {
		sanitizedPayload.is_published = payload.is_published as boolean
	}

	return sanitizedPayload
}

export const getListChangelog = async (params) => {
	return await axios.get(CHANGELOGROUTES.name, {
		params: convertParams(params),
	})
}

export const getOneChangelog = async (id: string, params = {}) => {
	return await axios.get(`${CHANGELOGROUTES.name}/${id}`, {
		params: convertParams(params),
	})
}

export const createChangelog = async (payload: Record<string, unknown>) => {
	const sanitizedPayload = sanitizeChangelogPayload(payload, true)
	return await axios.post(CHANGELOGROUTES.name, sanitizedPayload)
}

export const updateChangelog = async (
	id: string,
	payload: Record<string, unknown>,
) => {
	const sanitizedPayload = sanitizeChangelogPayload(payload)
	return await axios.put(`${CHANGELOGROUTES.name}/${id}`, sanitizedPayload)
}

export const deleteChangelog = async (id: string) => {
	return await axios.delete(`${CHANGELOGROUTES.name}/${id}`)
}

export const publishChangelog = async (id: string) => {
	return await axios.post(CHANGELOGROUTES.publish(id))
}

export const unpublishChangelog = async (id: string) => {
	return await axios.post(CHANGELOGROUTES.unpublish(id))
}
